import React, { useEffect } from "react";
import { Button, CircularProgress, Stack } from "@mui/material";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import SaveOutlinedIcon from "@mui/icons-material/SaveOutlined";
import { useTranslation } from "react-i18next";

import { useInlineEdit } from "./InlineEditContext";

/**
 * Page-level "Edit all" toggle. With pending changes it turns into
 * "Save all" (+ discard).
 */
const GlobalEditControls: React.FC = () => {
  const { t } = useTranslation();
  const {
    globalEdit,
    setGlobalEdit,
    dirtyTotal,
    isSavingAll,
    saveAll,
    discardAll,
  } = useInlineEdit();

  // Don't lose pending edits on a reload / tab close.
  useEffect(() => {
    if (dirtyTotal === 0) return undefined;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirtyTotal]);

  const handleSaveAll = async () => {
    if (await saveAll()) setGlobalEdit(false);
  };

  return (
    <Stack
      direction="row"
      spacing={1}
      alignItems="center"
      flexWrap="wrap"
      useFlexGap
    >
      {dirtyTotal > 0 ? (
        <>
          <Button
            variant="outlined"
            color="inherit"
            onClick={() => {
              discardAll();
              setGlobalEdit(false);
            }}
            disabled={isSavingAll}
          >
            {t("inlineEdit.discard", "Descartar")}
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveAll}
            disabled={isSavingAll}
            startIcon={
              isSavingAll ? (
                <CircularProgress size={14} color="inherit" />
              ) : (
                <SaveOutlinedIcon />
              )
            }
          >
            {t("inlineEdit.saveAll", {
              count: dirtyTotal,
              defaultValue: "Guardar todo ({{count}})",
            })}
          </Button>
        </>
      ) : (
        <Button
          variant={globalEdit ? "contained" : "outlined"}
          color={globalEdit ? "primary" : "inherit"}
          aria-pressed={globalEdit}
          onClick={() => setGlobalEdit(!globalEdit)}
          startIcon={globalEdit ? <CheckIcon /> : <EditOutlinedIcon />}
        >
          {globalEdit
            ? t("inlineEdit.doneEditing", "Terminar edición")
            : t("inlineEdit.editAll", "Editar todo")}
        </Button>
      )}
    </Stack>
  );
};

export default GlobalEditControls;
